/**
 * Componente de ações de seleção múltipla para Transações Financeiras
 *
 * Responsável por:
 * - Exibir número de itens selecionados
 * - Botão para cancelar seleção
 * - Botão para excluir múltiplos itens
 * - Confirmar exclusão múltipla
 *
 * @module App/FinancialTransactions/MultiSelectActions
 */
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, X } from "lucide-react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { deleteMultipleFinancialTransactions } from "@/Api/financialTransactions";
import { toast } from "sonner";
import { FinancialTransactionProps } from "@/types/financialTransaction";
import { ApiDataProps } from "@/pages/Tenants";
import { Mask } from "@/utils/mask";

interface MultiSelectActionsProps {
  selectedItems: FinancialTransactionProps[];
  onClearSelection: () => void;
  updateFront: React.Dispatch<
    React.SetStateAction<ApiDataProps<FinancialTransactionProps>>
  >;
}

export function MultiSelectActions({
  selectedItems,
  onClearSelection,
  updateFront,
}: MultiSelectActionsProps) {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteMultiple = async () => {
    if (selectedItems.length === 0) return;

    setIsDeleting(true);
    try {
      const uuids = selectedItems.map((item) => item.uuid);
      const responses = await deleteMultipleFinancialTransactions(uuids);

      if (responses.every((response) => response.status === 200)) {
        // Atualizar o frontend removendo os itens excluídos
        updateFront((prev) => ({
          ...prev,
          data: prev.data.filter((item) => !uuids.includes(item.uuid)),
        }));

        toast.success(`Excluídas com sucesso!`);
        onClearSelection();
      } else {
        toast.error("Erro ao excluir transações selecionadas");
      }
    } catch (error) {
      console.error("Erro ao excluir transações:", error);
      toast.error("Ocorreu um erro ao excluir as transações");
    } finally {
      setIsDeleting(false);
      setIsConfirmDialogOpen(false);
    }
  };

  const totalValue = selectedItems.reduce((sum, item) => sum + item.amount, 0);

  return (
    <>
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
            {selectedItems.length} item{selectedItems.length > 1 ? "s" : ""}{" "}
            selecionado{selectedItems.length > 1 ? "s" : ""}
          </Badge>
          <span className="text-sm text-muted-foreground">
            Valor total: {Mask.formatCurrency(totalValue)}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            className="text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-4 w-4 mr-1" />
            Cancelar
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setIsConfirmDialogOpen(true)}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Excluir Selecionadas
          </Button>
        </div>
      </div>

      {/* Modal de confirmação */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir {selectedItems.length} transação
              {selectedItems.length > 1 ? "ões" : ""}? Esta ação não pode ser
              desfeita.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-60 overflow-y-auto space-y-2">
            {selectedItems.map((transaction) => (
              <div
                key={transaction.id}
                className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800 rounded"
              >
                <span className="text-sm font-medium">
                  {transaction.description}
                </span>
                <span className="text-sm text-muted-foreground">
                  {Mask.formatCurrency(transaction.amount)}
                </span>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConfirmDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteMultiple}
              disabled={isDeleting}
            >
              {isDeleting ? "Excluindo..." : "Confirmar Exclusão"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
